import { useSelector } from "react-redux";

import {
	CROSS_SKILLING_PERMISSIONS,
	JOBS_MATRIX_PERMISSIONS,
} from "../../../config/permissions";
import { hasPermissionString } from "../../../utils/helpers";
import { StoreType } from "../../../utils/types";

export interface CrossSkillingAccess {
	canViewSkillsMatrix: boolean;
	canViewSummary: boolean;
	canViewJobsMatrix: boolean;
	/** True when at least one tab is visible, i.e. the page has content. */
	canViewPage: boolean;
}

// Single source for which Cross Skilling tabs a user can see. The drawer
// entry, the route redirect and the tab bar all read this, so the menu never
// links to a page that would bounce the user back out.
//
// Every tab needs a permission on top of its company flags: Skills Matrix
// (GAT-7254), Summary (GAT-7253) and Jobs Matrix (GAT-7255).
const useCrossSkillingAccess = (): CrossSkillingAccess => {
	const showCrossSkilling = useSelector(
		(state: StoreType) => state.user.userData.showCrossSkilling
	);
	const isSkillInsightsEnabled = useSelector(
		(state: StoreType) => state.user.userData.skillInsightsEnabled
	);
	const isJobsMatrixEnabled = useSelector(
		(state: StoreType) => state.user.userData.jobsMatrixEnabled
	);
	const permissions = useSelector(
		(state: StoreType) => state.user?.userData?.permissions
	);

	const canViewSkillsMatrix =
		!!showCrossSkilling &&
		hasPermissionString(permissions, CROSS_SKILLING_PERMISSIONS.VIEW);
	const canViewSummary =
		!!showCrossSkilling &&
		!!isSkillInsightsEnabled &&
		hasPermissionString(permissions, CROSS_SKILLING_PERMISSIONS.VIEW_SUMMARY);
	const canViewJobsMatrix =
		!!isJobsMatrixEnabled &&
		hasPermissionString(permissions, JOBS_MATRIX_PERMISSIONS.VIEW);

	return {
		canViewSkillsMatrix,
		canViewSummary,
		canViewJobsMatrix,
		canViewPage: canViewSkillsMatrix || canViewSummary || canViewJobsMatrix,
	};
};

export default useCrossSkillingAccess;
